import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useProject } from '../context/ProjectContext';
import { useToast } from '../hooks/useToast';
import { db, supabase } from '../services';
import { User, Role } from '../types';

const Profile: React.FC = () => {
  const { currentUser: user, setCurrentUser, isInvited, updatePassword: authUpdatePassword } = useAuth();
  const { refreshAll, tasks, projects } = useProject();
  const { showSuccess, showError } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isEditing, setIsEditing] = useState(false);
  const [passwords, setPasswords] = useState({ current: '', new: '', confirm: '' });
  const [profileData, setProfileData] = useState({ name: user?.name || '', email: user?.email || '' });
  const [message, setMessage] = useState({ text: '', type: 'success' });
  const [isProcessing, setIsProcessing] = useState(false);

  // Sync profile data when context user changes
  useEffect(() => {
    if (user) {
      setProfileData({ name: user.name, email: user.email });
    }
  }, [user]);

  if (!user) return null;

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64String = reader.result as string;
      try {
        await db.updateUser(user.id, { avatar: base64String });
        setCurrentUser({ ...user, avatar: base64String });
        setMessage({ text: 'Profile picture updated!', type: 'success' });
      } catch (err) {
        setMessage({ text: 'Failed to save avatar to cloud.', type: 'error' });
      }
    };
    reader.readAsDataURL(file);
  };

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsProcessing(true);
    try {
      await db.updateUser(user.id, profileData);
      setCurrentUser({ ...user, ...profileData });
      setMessage({ text: 'Profile updated successfully!', type: 'success' });
      setIsEditing(false);
    } catch (err) {
      setMessage({ text: 'Failed to update profile.', type: 'error' });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleInitialSetup = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage({ text: '', type: 'success' });

    if (passwords.new !== passwords.confirm) {
      setMessage({ text: 'Passwords do not match.', type: 'error' });
      return;
    }
    if (passwords.new.length < 8) {
      setMessage({ text: 'Password must be at least 8 characters.', type: 'error' });
      return;
    }

    setIsProcessing(true);
    try {
      // 1. Update Auth (Password + Metadata)
      await db.updatePassword(passwords.new);
      
      // 2. Fetch the updated session immediately to reflect metadata changes
      const { data: { session } } = await supabase.auth.getSession();
      
      setMessage({ text: 'Account activated! Handshaking with cloud...', type: 'success' });
      
      // 3. Trigger a full app context refresh
      if (refreshAll) {
        await refreshAll();
      }

      // 4. Force a slight delay before reload to let metadata propagate
      setTimeout(() => {
        window.location.reload();
      }, 1500);
      
    } catch (err: any) {
      console.error("Activation failed:", err);
      setMessage({ text: err.message || "Activation failed. Please try again.", type: 'error' });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (passwords.new !== passwords.confirm) {
      setMessage({ text: 'New passwords do not match.', type: 'error' });
      return;
    }
    setIsProcessing(true);
    try {
      await db.updatePassword(passwords.new);
      setMessage({ text: 'Password updated successfully!', type: 'success' });
      setPasswords({ current: '', new: '', confirm: '' });
    } catch (err: any) {
      setMessage({ text: err.message, type: 'error' });
    } finally {
      setIsProcessing(false);
    }
  };

  // If the user still has the 'invited' flag in metadata, show setup screen
  if (isInvited) {
    return (
      <div className="max-w-xl mx-auto py-12 px-6 animate-in fade-in zoom-in-95 overflow-y-auto h-full">
        <div className="bg-white rounded-[2.5rem] shadow-2xl border border-slate-100 p-10 md:p-12">
          <div className="w-20 h-20 bg-indigo-600 text-white rounded-3xl flex items-center justify-center mb-8 shadow-xl shadow-indigo-500/20 mx-auto">
            <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/></svg>
          </div>
          <div className="text-center mb-10">
            <h1 className="text-3xl font-black text-slate-900 tracking-tight">Complete Your Setup</h1>
            <p className="text-slate-500 font-medium mt-2">Welcome to the organization! Please choose a secure password to activate your account.</p>
          </div>

          <form onSubmit={handleInitialSetup} className="space-y-6">
            {message.text && (
              <div className={`p-4 rounded-2xl border text-sm font-bold animate-in slide-in-from-top-2 ${message.type === 'success' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-rose-50 text-rose-700 border-rose-100'}`}>
                {message.text}
              </div>
            )}
            
            <div>
              <label className="block text-[11px] font-black uppercase tracking-widest text-slate-400 mb-2 ml-1">New Secure Password</label>
              <input 
                type="password" 
                required
                value={passwords.new}
                onChange={(e) => setPasswords({...passwords, new: e.target.value})}
                className="w-full px-6 py-4 rounded-2xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-indigo-500 outline-none transition-all font-bold text-slate-900"
                placeholder="Minimum 8 characters"
              />
            </div>
            <div>
              <label className="block text-[11px] font-black uppercase tracking-widest text-slate-400 mb-2 ml-1">Confirm Password</label>
              <input 
                type="password" 
                required
                value={passwords.confirm}
                onChange={(e) => setPasswords({...passwords, confirm: e.target.value})}
                className="w-full px-6 py-4 rounded-2xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-indigo-500 outline-none transition-all font-bold text-slate-900"
                placeholder="Repeat password"
              />
            </div>

            <button 
              type="submit"
              disabled={isProcessing}
              className="w-full py-5 bg-indigo-600 text-white font-black uppercase tracking-widest text-xs rounded-2xl hover:bg-indigo-700 transition-all shadow-xl shadow-indigo-500/20 flex items-center justify-center gap-3 active:scale-95 disabled:opacity-70"
            >
              {isProcessing ? (
                <>
                   <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                   Activating...
                </>
              ) : 'Activate My Account'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500 space-y-8 pb-20 overflow-y-auto h-full p-4 md:p-8">
      <header className="flex items-center gap-6">
        <div className="relative group cursor-pointer" onClick={handleAvatarClick}>
          <div className="relative overflow-hidden rounded-[2.5rem] w-24 h-24 md:w-32 md:h-32 border-4 border-white shadow-xl">
            <img 
              src={user.avatar} 
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" 
              alt={user.name} 
            />
            <div className="absolute inset-0 bg-indigo-900/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
               <span className="text-white text-[10px] font-black uppercase tracking-widest">Update</span>
            </div>
          </div>
          <button className="absolute bottom-1 right-1 p-2.5 bg-indigo-600 text-white rounded-2xl shadow-lg border-2 border-white hover:bg-indigo-700 hover:scale-110 transition-all z-10">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"/>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"/>
            </svg>
          </button>
          <input 
            type="file" 
            ref={fileInputRef} 
            className="hidden" 
            accept="image/*" 
            onChange={handleAvatarChange}
          />
        </div>
        <div>
          <h1 className="text-3xl md:text-4xl font-black text-slate-900 tracking-tight">{user.name}</h1>
          <div className="flex items-center gap-2 mt-2">
            <span className="px-3 py-1 bg-indigo-50 text-indigo-600 text-[10px] font-black uppercase tracking-widest rounded-lg border border-indigo-100">{user.role.replace('_', ' ')}</span>
            <span className="text-slate-400 text-sm font-medium">• {user.email}</span>
          </div>
        </div>
      </header>

      {message.text && (
        <div className={`p-4 rounded-2xl border animate-in zoom-in-95 ${message.type === 'success' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-rose-50 text-rose-700 border-rose-100'}`}>
          <div className="flex items-center gap-3">
             <span className="font-bold text-sm">{message.text}</span>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <section className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm p-8 md:p-10">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-xl font-black text-slate-900">Personal Details</h2>
            <button 
              onClick={() => setIsEditing(!isEditing)}
              className="text-xs font-black text-indigo-600 uppercase tracking-widest hover:underline"
            >
              {isEditing ? 'Cancel' : 'Edit Profile'}
            </button>
          </div>
          <form onSubmit={handleUpdateProfile} className="space-y-6">
            <div>
              <label className="block text-[11px] font-black uppercase tracking-widest text-slate-400 mb-2 ml-1">Full Name</label>
              <input 
                type="text" 
                value={profileData.name}
                disabled={!isEditing}
                onChange={(e) => setProfileData({...profileData, name: e.target.value})}
                className="w-full px-5 py-4 rounded-2xl border border-slate-200 bg-slate-50/50 focus:bg-white focus:border-indigo-500 outline-none transition-all disabled:opacity-50 font-semibold text-slate-900 shadow-sm"
              />
            </div>
            <div>
              <label className="block text-[11px] font-black uppercase tracking-widest text-slate-400 mb-2 ml-1">Email Address</label>
              <input 
                type="email" 
                value={profileData.email}
                disabled
                className="w-full px-5 py-4 rounded-2xl border border-slate-200 bg-slate-50 opacity-50 font-semibold text-slate-900 shadow-sm"
              />
            </div>
            {isEditing && (
              <button 
                type="submit"
                disabled={isProcessing}
                className="w-full py-4 bg-indigo-600 text-white font-black uppercase tracking-widest text-[11px] rounded-2xl hover:bg-indigo-700 shadow-xl shadow-indigo-500/20"
              >
                Save Changes
              </button>
            )}
          </form>
        </section>

        <section className="bg-white rounded-[2.5rem] border border-slate-100 shadow-sm p-8 md:p-10">
          <h2 className="text-xl font-black text-slate-900 mb-8">Security</h2>
          <form onSubmit={handleChangePassword} className="space-y-6">
            <div>
              <label className="block text-[11px] font-black uppercase tracking-widest text-slate-400 mb-2 ml-1">New Password</label>
              <input 
                type="password" 
                value={passwords.new}
                onChange={(e) => setPasswords({...passwords, new: e.target.value})}
                className="w-full px-5 py-4 rounded-2xl border border-slate-200 bg-slate-50/50 focus:bg-white focus:border-indigo-500 outline-none transition-all font-semibold text-slate-900 shadow-sm"
                placeholder="••••••••"
                required
              />
            </div>
            <div>
              <label className="block text-[11px] font-black uppercase tracking-widest text-slate-400 mb-2 ml-1">Confirm New</label>
              <input 
                type="password" 
                value={passwords.confirm}
                onChange={(e) => setPasswords({...passwords, confirm: e.target.value})}
                className="w-full px-5 py-4 rounded-2xl border border-slate-200 bg-slate-50/50 focus:bg-white focus:border-indigo-500 outline-none transition-all font-semibold text-slate-900 shadow-sm"
                placeholder="••••••••"
                required
              />
            </div>
            <button 
              type="submit"
              disabled={isProcessing}
              className="w-full py-4 bg-slate-900 text-white font-black uppercase tracking-widest text-[11px] rounded-2xl hover:bg-slate-800 transition-all shadow-xl shadow-slate-900/10"
            >
              Update Password
            </button>
          </form>
        </section>

        <section className="bg-[#1e293b] text-white rounded-[2.5rem] p-8 md:p-10 lg:col-span-2 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/10 rounded-full blur-3xl -mr-32 -mt-32" />
          <div className="relative z-10">
            <h2 className="text-xl font-black mb-8">Platform Activity</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { label: 'Member Since', value: 'Recent' },
                { label: 'Assigned Tasks', value: tasks.filter((t: any) => t.assigneeId === user.id).length || 0 },
                { label: 'Total Projects', value: projects.filter((p: any) => p.memberIds.includes(user.id)).length || 0 },
                { label: 'Security', value: 'Active' }
              ].map((stat) => (
                <div key={stat.label} className="bg-white/5 border border-white/10 p-5 rounded-3xl backdrop-blur-md">
                  <p className="text-[10px] font-black uppercase tracking-widest text-indigo-300 mb-1">{stat.label}</p>
                  <p className="text-2xl font-black">{stat.value}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Profile;